import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Workflow, Zap, Link2, GraduationCap, CheckCircle2 } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const STEPS = [{
  icon: Workflow,
  title: 'Discovery & Mapping',
  desc: 'We sit down with your team to understand your unique processes, bottlenecks, and the tools you rely on daily.'
}, {
  icon: Zap,
  title: 'Custom Build-out',
  desc: 'Our engineers design and deploy custom automations and agents tailored specifically to your mapped workflows.'
}, {
  icon: Link2,
  title: 'Deep Integration',
  desc: 'We wire Giga directly into your existing stack—whether it’s legacy on-prem systems or modern SaaS.'
}, {
  icon: GraduationCap,
  title: 'Training & Tuning',
  desc: 'We onboard your team, run workshops, and continuously refine the agents as your operations evolve.'
}];
const OUTCOMES = [{
  title: 'Automated Client Reporting',
  desc: 'Giga pulls data from Salesforce, Stripe, and internal databases to generate and email weekly client reports without human intervention.'
}, {
  title: 'RevOps Pipelines',
  desc: 'Seamlessly sync lead data across marketing and sales tools, automatically triggering personalized outreach sequences.'
}, {
  title: 'Support Triage',
  desc: 'Intelligently categorize, route, and draft initial responses for incoming support tickets based on historical resolution data.'
}, {
  title: 'Finance Reconciliation',
  desc: 'Automatically match invoices with bank statements and flag discrepancies for human review, saving hours of manual data entry.'
}];
export function Bespoke() {
  useScreenInit();
  return <div className="min-h-screen bg-[#1C1510] flex flex-col pt-24 pb-12 relative overflow-hidden" data-id="element-3619">
      {/* Dark texture overlay */}
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none z-0" data-id="element-3620" />

      {/* Soft warm glow */}
      <div aria-hidden="true" className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px] z-0" data-id="element-3621" />

      <div className="relative z-10 flex-1 flex flex-col" data-id="element-3622">
        {/* Hero Section */}
        <section className="px-6 md:px-10 py-16 md:py-24 max-w-5xl mx-auto text-center" data-id="element-3623">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-3624">
            <span className="inline-block text-xs font-bold tracking-[3px] uppercase text-[#EA580C] mb-6" data-id="element-3625">
              Custom Engagement
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-title font-bold text-white tracking-tight leading-[1.1] mb-8" data-id="element-3626">
              Your workflows, <br className="hidden md:block" data-id="element-3627" />
              <span className="text-neutral-400" data-id="element-3628">built by our team.</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-3629">
              For teams with complex operations. We don't just hand you a tool;
              we partner with you to map, build, and deploy custom AI agents
              that integrate deeply into your stack.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3630">
              <Link to="/demo/book" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-lg shadow-[#EA580C]/20 flex items-center justify-center gap-2" data-id="element-3631">
                Book a call
                <ArrowRight size={18} data-id="element-3632" />
              </Link>
              <Link to="/contact" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold transition-colors flex items-center justify-center" data-id="element-3633">
                Contact us
              </Link>
            </div>
          </motion.div>
        </section>

        {/* How it Works */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-[#2B211A]/50 border-y border-white/5" data-id="element-3634">
          <div className="max-w-6xl mx-auto" data-id="element-3635">
            <div className="text-center mb-16" data-id="element-3636">
              <h2 className="text-3xl md:text-4xl font-title font-bold text-white mb-4" data-id="element-3637">
                How the engagement works
              </h2>
              <p className="text-neutral-400 max-w-2xl mx-auto" data-id="element-3638">
                A structured, hands-on approach to automating your most critical
                business processes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3639">
              {STEPS.map((step, i) => {
              const Icon = step.icon;
              return <motion.div key={i} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.5,
                delay: i * 0.1
              }} className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden group hover:border-[#EA580C]/50 transition-colors" data-id="element-3640">
                    <div className="absolute top-0 right-0 p-4 text-[100px] font-title font-bold text-white/[0.02] leading-none select-none pointer-events-none group-hover:text-[#EA580C]/[0.05] transition-colors" data-id="element-3641">
                      {i + 1}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#1C1510] border border-white/10 flex items-center justify-center mb-6 relative z-10" data-id="element-3642">
                      <Icon className="w-6 h-6 text-[#EA580C]" data-id="element-3643" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-3644">
                      {step.title}
                    </h3>
                    <p className="text-sm text-neutral-400 leading-relaxed relative z-10" data-id="element-3645">
                      {step.desc}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section className="px-6 md:px-10 py-16 md:py-24 max-w-6xl mx-auto" data-id="element-3646">
          <div className="mb-16" data-id="element-3647">
            <h2 className="text-3xl md:text-4xl font-title font-bold text-white mb-4" data-id="element-3648">
              What we build
            </h2>
            <p className="text-neutral-400 max-w-2xl" data-id="element-3649">
              Examples of custom solutions we've deployed for enterprise
              partners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3650">
            {OUTCOMES.map((outcome, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: i % 2 === 0 ? -20 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="flex gap-4 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/[0.07] transition-colors" data-id="element-3651">
                <div className="mt-1 shrink-0" data-id="element-3652">
                  <CheckCircle2 className="w-6 h-6 text-[#EA580C]" data-id="element-3653" />
                </div>
                <div data-id="element-3654">
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-3655">
                    {outcome.title}
                  </h3>
                  <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-3656">
                    {outcome.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* Proof & Closing CTA */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-[#EA580C] text-center mt-auto" data-id="element-3657">
          <div className="max-w-3xl mx-auto" data-id="element-3658">
            <h2 className="text-3xl md:text-5xl font-title font-bold text-white mb-6" data-id="element-3659">
              Ready to automate your operations?
            </h2>
            <p className="text-lg text-white/80 mb-10" data-id="element-3660">
              Let's discuss your workflows and see if a Custom engagement is
              right for your team.
            </p>
            <Link to="/demo/book" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#1C1510] hover:bg-black text-white font-bold transition-colors shadow-xl" data-id="element-3661">
              Talk to us
              <ArrowRight size={18} data-id="element-3662" />
            </Link>
          </div>
        </section>
      </div>
    </div>;
}